import { type CSSProperties, type FC, useRef } from "react";
import { useColorArea, useFocusRing } from "react-aria";
import { type ColorAreaProps as AriaColorAreaProps, useColorAreaState } from "react-stately";

import {
  COLOR_AREA_BORDER_RADIUS,
  COLOR_AREA_FOCUSED_THUMB_SIZE,
  COLOR_AREA_SIZE,
  COLOR_AREA_THUMB_SIZE,
} from "./settings.ts";

export interface ColorAreaProps extends AriaColorAreaProps {
  size?: [number, number];
  style?: CSSProperties;
}

export const ColorArea: FC<ColorAreaProps> = (props) => {
  const inputXRef = useRef(null);
  const inputYRef = useRef(null);
  const containerRef = useRef(null);

  const state = useColorAreaState(props);

  const { isDisabled } = props;

  const { colorAreaProps, xInputProps, yInputProps, thumbProps } = useColorArea(
    { ...props, inputXRef, inputYRef, containerRef },
    state,
  );

  const { focusProps, isFocusVisible } = useFocusRing();

  return (
    <div
      data-testid="color-area"
      ref={containerRef}
      {...colorAreaProps}
      style={{
        ...colorAreaProps.style,
        width: props.size?.[0] ?? COLOR_AREA_SIZE,
        height: props.size?.[1] ?? COLOR_AREA_SIZE,
        borderRadius: COLOR_AREA_BORDER_RADIUS,
        opacity: isDisabled ? 0.3 : undefined,
        ...props.style,
      }}
    >
      <div
        data-testid="color-area-thumb"
        {...thumbProps}
        style={{
          ...thumbProps.style,
          background: isDisabled ? "rgb(142, 142, 142)" : state.getDisplayColor().toString("css"),
          border: `2px solid ${isDisabled ? "rgb(142, 142, 142)" : "white"}`,
          borderRadius: "50%",
          boxShadow: "0 0 0 1px black, inset 0 0 0 1px black",
          boxSizing: "border-box",
          height: isFocusVisible ? COLOR_AREA_FOCUSED_THUMB_SIZE + 4 : COLOR_AREA_THUMB_SIZE,
          transform: "translate(-50%, -50%)",
          width: isFocusVisible ? COLOR_AREA_FOCUSED_THUMB_SIZE + 4 : COLOR_AREA_THUMB_SIZE,
        }}
      >
        <input ref={inputXRef} {...xInputProps} {...focusProps} />
        <input ref={inputYRef} {...yInputProps} {...focusProps} />
      </div>
    </div>
  );
};
